import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Snowflake, Thermometer, Lightning, Gauge, DotsThreeVertical, Trash, ArrowUUpLeft } from "@phosphor-icons/react";
import FdReadingForm from "@/components/FdReadingForm";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

function Tray({ t, machineId, onDrop, onDragStart, draggable, isDragging }) {
  if (!t.occupied) {
    return (
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => onDrop(e, t.tray_no)}
        className="aspect-square rounded-sm border border-dashed border-border bg-muted/30 flex items-center justify-center text-[9px] text-muted-foreground/60 mono hover:border-primary hover:bg-primary/5 transition-colors"
        data-testid={`tray-empty-${machineId}-${t.tray_no}`}
      >
        {t.tray_no}
      </div>
    );
  }
  return (
    <div
      draggable={draggable}
      onDragStart={e => onDragStart(e, t)}
      className={`aspect-square rounded-sm border ${isDragging ? "opacity-40 border-dashed" : "border-primary"} bg-primary/10 flex flex-col items-center justify-center text-[9px] font-bold text-primary p-0.5 overflow-hidden ${draggable ? "cursor-move" : "cursor-help"}`}
      title={`Tray ${t.tray_no}\n${t.batch_no}\n${t.sku_name}\n${t.weight_kg} kg`}
      data-testid={`tray-${t.tray_no}`}
    >
      <div className="mono text-[8px] opacity-70">{t.tray_no}</div>
      <div className="mono text-[9px] leading-tight truncate w-full text-center">{t.sku_code}</div>
    </div>
  );
}

const LATEST = {};

function TelemetryCard({ machine }) {
  const [points, setPoints] = useState([]);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/machines/${machine.id}/telemetry?limit=30`);
        setPoints(data.map(d => ({
          t: new Date(d.at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"}),
          vacuum: d.vacuum_mbar, shelf: d.shelf_temp_c, power: d.power_kw
        })));
        setLatest(data[data.length - 1] || null);
      } catch {}
    };
    fetch();
    const t = setInterval(fetch, 15000);
    return () => clearInterval(t);
  }, [machine.id]);

  const simulate = async () => {
    // Local IoT simulator for demo
    const body = {
      vacuum_mbar: +(Math.random() * 1.2 + 0.2).toFixed(2),
      shelf_temp_c: +(Math.random() * 10 - 30).toFixed(1),
      chamber_temp_c: +(Math.random() * 15 - 45).toFixed(1),
      power_kw: +(Math.random() * 4 + 6).toFixed(1),
      cycle_status: "Running",
    };
    await api.post(`/machines/${machine.id}/telemetry`, body);
    toast.success("Telemetry pushed");
  };

  if (!latest && points.length === 0) {
    return (
      <div className="border-t border-border pt-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">IoT Telemetry</div>
          <button className="text-[10px] font-semibold text-primary hover:underline" onClick={simulate} data-testid={`sim-telemetry-${machine.code}`}>Simulate reading</button>
        </div>
        <div className="text-xs text-muted-foreground text-center py-4">No sensor readings yet</div>
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">IoT Telemetry · Live</div>
        <button className="text-[10px] font-semibold text-primary hover:underline" onClick={simulate} data-testid={`sim-telemetry-${machine.code}`}>+ reading</button>
      </div>
      {latest && (
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="p-2 rounded-md border border-border">
            <div className="overline flex items-center gap-1"><Gauge size={10}/>Vacuum</div>
            <div className={`text-metric text-lg ${latest.vacuum_mbar > 1.5 ? "text-red-600" : "text-primary"}`}>{latest.vacuum_mbar}<span className="text-[10px]">mbar</span></div>
          </div>
          <div className="p-2 rounded-md border border-border">
            <div className="overline flex items-center gap-1"><Thermometer size={10}/>Shelf</div>
            <div className="text-metric text-lg">{latest.shelf_temp_c}<span className="text-[10px]">°C</span></div>
          </div>
          <div className="p-2 rounded-md border border-border">
            <div className="overline flex items-center gap-1"><Lightning size={10}/>Power</div>
            <div className="text-metric text-lg">{latest.power_kw}<span className="text-[10px]">kW</span></div>
          </div>
        </div>
      )}
      {points.length > 1 && (
        <div className="h-32">
          <ResponsiveContainer>
            <LineChart data={points}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
              <XAxis dataKey="t" fontSize={9} stroke="hsl(var(--muted-foreground))"/>
              <YAxis fontSize={9} stroke="hsl(var(--muted-foreground))"/>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11 }}/>
              <Line type="monotone" dataKey="vacuum" stroke="hsl(var(--chart-1))" strokeWidth={1.5} dot={false} name="Vacuum"/>
              <Line type="monotone" dataKey="shelf" stroke="hsl(var(--chart-2))" strokeWidth={1.5} dot={false} name="Shelf °C"/>
              <Line type="monotone" dataKey="power" stroke="hsl(var(--chart-3))" strokeWidth={1.5} dot={false} name="Power"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function MachineCard({ machine, trays, batches, reload }) {
  const cols = machine.trays >= 60 ? 10 : 8;
  const [dragging, setDragging] = useState(null);

  const handleDragStart = (e, t) => {
    setDragging(t);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = async (e, targetTray) => {
    e.preventDefault();
    if (!dragging) return;
    // Find batch owning the source tray and reassign
    const b = batches.find(x => x.machine_id === machine.id && (x.tray_numbers || []).includes(dragging.tray_no));
    if (!b) { setDragging(null); return; }
    const newTrays = b.tray_numbers.map(t => t === dragging.tray_no ? targetTray : t);
    try {
      await api.post(`/batches/${b.id}/allocate-trays`, { machine_id: machine.id, tray_numbers: newTrays });
      toast.success(`Tray ${dragging.tray_no} → ${targetTray}`);
      reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reassignment failed");
    } finally { setDragging(null); }
  };

  return (
    <Card className="shadow-none" data-testid={`machine-${machine.code}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center">
              <Snowflake size={24} weight="duotone"/>
            </div>
            <div>
              <div className="overline">{machine.code}</div>
              <h3 className="font-display font-bold text-xl">{machine.name}</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={machine.status}/>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`machine-actions-${machine.code}`}>
                  <DotsThreeVertical size={16} weight="bold"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {machine.status !== "inactive" ? (
                  <DropdownMenuItem className="text-red-600" data-testid={`machine-delete-${machine.code}`}
                    onSelect={async () => {
                      if (!window.confirm(`Deactivate ${machine.name}? Batches already produced on it are preserved.`)) return;
                      try { const r = await api.delete(`/machines/${machine.id}`);
                            toast.success(r.data.message || "Machine deactivated."); reload(); }
                      catch (e) { toast.error(e.response?.data?.detail || e.message); }
                    }}>
                    <Trash size={14} className="mr-2"/>Deactivate machine
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem className="text-emerald-700" data-testid={`machine-restore-${machine.code}`}
                    onSelect={async () => {
                      try { await api.post(`/machines/${machine.id}/restore`);
                            toast.success("Machine restored."); reload(); }
                      catch (e) { toast.error(e.response?.data?.detail || e.message); }
                    }}>
                    <ArrowUUpLeft size={14} className="mr-2"/>Restore machine
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4">
          <div><div className="overline">Capacity</div><div className="text-metric text-lg">{machine.capacity_kg}<span className="text-xs">kg</span></div></div>
          <div><div className="overline">Trays</div><div className="text-metric text-lg">{machine.trays}</div></div>
          <div><div className="overline">Cycle</div><div className="text-metric text-lg">{machine.cycle_hours}<span className="text-xs">h</span></div></div>
          <div><div className="overline">Occupied</div><div className="text-metric text-lg text-primary">{trays.filter(t=>t.occupied).length}</div></div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-[11px] mb-1">
            <span className="font-semibold">Tray Utilization</span>
            <span className="mono">{trays.filter(t=>t.occupied).length}/{machine.trays}</span>
          </div>
          <Progress value={(trays.filter(t=>t.occupied).length / machine.trays) * 100} className="h-1.5"/>
        </div>

        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Tray Layout <span className="normal-case tracking-normal text-muted-foreground">· drag to reassign</span></div>
          </div>
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {trays.map(t => (
              <Tray key={t.tray_no}
                t={t}
                machineId={machine.id}
                draggable={t.occupied}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                isDragging={dragging?.tray_no === t.tray_no}
              />
            ))}
          </div>
        </div>

        <TelemetryCard machine={machine}/>

        <FdReadingForm
          machine={machine}
          activeBatch={batches.find(b => b.stage === "FreezeDrying") || null}
          onApproved={reload}
        />

        {batches.length > 0 && (
          <div className="border-t border-border pt-4 mt-4">
            <div className="overline mb-2">Active Batches</div>
            <div className="space-y-2">
              {batches.map(b => (
                <div key={b.id} className="flex justify-between items-center text-sm">
                  <div>
                    <div className="mono font-semibold">{b.batch_no}</div>
                    <div className="text-xs text-muted-foreground">{b.sku_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="mono text-sm">{b.planned_qty_kg} kg</div>
                    <StatusBadge status={b.stage}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Machines() {
  const [machines, setMachines] = useState([]);
  const [trayMap, setTrayMap] = useState({});
  const [batches, setBatches] = useState([]);

  const load = async () => {
    const { data: ms } = await api.get("/machines");
    setMachines(ms);
    const { data: bs } = await api.get("/batches");
    setBatches(bs);
    const tm = {};
    for (const m of ms) {
      try {
        const { data } = await api.get(`/machines/${m.id}/trays`);
        tm[m.id] = data.trays;
      } catch { tm[m.id] = []; }
    }
    setTrayMap(tm);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="machines-page">
      <PageHeader subtitle="Machine Scheduling" title="Freeze Dryer Management"/>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {machines.map(m => (
          <MachineCard
            key={m.id}
            machine={m}
            trays={trayMap[m.id] || []}
            batches={batches.filter(b => b.machine_id === m.id && b.status === "In Progress")}
            reload={load}
          />
        ))}
      </div>
    </div>
  );
}
